import { ProgramEngagement, ProgramEngagementModel } from '@cibo/core'
import { Loading } from '@cibo/profile'
import { useCreateProgramEngagement } from '@cibo/programs'
import { ContentfulImage, logRequestError } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import { Box, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import { Markdown } from '../../consts/Theme'
import { useProgramEngagementContext } from '../../features/ProgramEngagement'
import { ENGAGEMENT_ROUTES } from '../../navigation/types/engagementRoutes'

export type ProgramInfoOnStartParams = {
  engagement: ProgramEngagement
}

type ProgramInfoProps = {
  hideGetStartedButton?: boolean
}

export const ProgramInfo = ({ hideGetStartedButton }: ProgramInfoProps) => {
  const { t } = useTranslation('@cibo/impact/ProgramInfo')
  const { basePath, program, userId } = useProgramEngagementContext()
  const createEngagement = useCreateProgramEngagement()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const handleClickStart = () => {
    program.data?.programId &&
      createEngagement
        .mutateAsync({
          userId,
          programId: program.data.programId,
          doesUpsert: true,
        })
        .then(engagement => {
          const engagementModel = new ProgramEngagementModel(engagement.data)
          navigate(
            generatePath(`${basePath}${ENGAGEMENT_ROUTES.STEP}`, {
              stageId: 'apply',
              engagementId: engagement.data.id,
              stepId: engagementModel.currentStepId,
            })
          )
        })
        .catch(error => {
          logRequestError(error)
          enqueueSnackbar(error.message, { variant: 'error' })
        })
  }

  if (!program || program?.isLoading) {
    return <Loading />
  }

  return (
    <Stack alignItems={'flex-start'}>
      {!!program.data?.program.content.fields.promoImages?.[0] && (
        <ContentfulImage
          asset={program.data.program.content.fields.promoImages?.[0]}
          height={400}
          style={{ objectFit: 'cover', height: 147, width: '100%' }}
        />
      )}
      <Stack p={3} spacing={3} alignItems={'flex-start'}>
        <Typography variant="h6">{program.data?.program.content.fields.title}</Typography>
        <Box>
          <Markdown color="text.secondary">
            {program.data?.program.content.fields.longDescription}
          </Markdown>
        </Box>
        {!hideGetStartedButton && (
          <LoadingButton
            onClick={handleClickStart}
            loading={createEngagement.isLoading}
            variant="contained"
          >
            {t('getStarted')}
          </LoadingButton>
        )}
      </Stack>
    </Stack>
  )
}
